import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Box,
  Container,
  Typography,
  Card,
  Alert,
  Snackbar,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  Divider,
  DialogActions,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Collapse,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { LocalizationProvider, TimePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import es from "date-fns/locale/es";
import {
  DirectionsCar,
  CheckCircle,
  PlayArrow,
  Stop,
  ExpandMore,
  ExpandLess,
  Check,
  Close,
} from "@mui/icons-material";

const GestionPases = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [vehiculoInfo, setVehiculoInfo] = useState(null);
  const [paseInfo, setPaseInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [usuarioId] = useState(1);
  const [historicoRegistros, setHistoricoRegistros] = useState([]);
  const [checklistExpanded, setChecklistExpanded] = useState(true);
  const [checklistRetornoExpanded, setChecklistRetornoExpanded] =
    useState(true);
  const [conductores, setConductores] = useState([]);
  const [rutas, setRutas] = useState([]);
  const [registroSalidaExistente, setRegistroSalidaExistente] = useState(null);
  const [tipoBusqueda, setTipoBusqueda] = useState("placa"); // "placa" o "vin"

  // Formulario placa y VIN
  const [formBusqueda, setFormBusqueda] = useState({
    placa: "",
    vin: "",
  });

  // Form salida
  const [formSalida, setFormSalida] = useState({
    kilometraje_inicio: "",
    observaciones_salida: "",
    hora_salida: new Date(),
    nombre_guardia: "",
    // Checklist de seguridad - valores booleanos
    llanta: false,
    jack: false,
    llave: false,
    triangulos: false,
    extintor: false,
  });

  // Form retorno
  const [formRetorno, setFormRetorno] = useState({
    kilometraje_fin: "",
    observaciones_retorno: "",
    hora_retorno: new Date(),
    nombre_guardia: "",
    // Checklist de seguridad de retorno - valores booleanos
    llanta: false,
    jack: false,
    llave: false,
    triangulos: false,
    extintor: false,
  });

  // Notificación
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Confirm dialog
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    message: "",
    onConfirm: () => {},
  });

  // Cargar lista de conductores al iniciar
  useEffect(() => {
    const fetchConductores = async () => {
      try {
        const response = await fetch(
          "https://bei360.injupemp.gob.hn:8444/api/conductores/"
        );
        if (response.ok) {
          const data = await response.json();
          setConductores(data);
        }
      } catch (error) {
        console.error("Error al cargar conductores:", error);
      }
    };
    fetchConductores();
  }, []);

  // Cargar lista de rutas
  useEffect(() => {
    const fetchRutas = async () => {
      try {
        const response = await fetch(
          "https://bei360.injupemp.gob.hn:8444/api/rutas/"
        );
        if (response.ok) {
          const data = await response.json();
          setRutas(data);
        }
      } catch (error) {
        console.error("Error al cargar conductores:", error);
      }
    };
    fetchRutas();
  }, []);

  // Manejador de cambios en el tipo de búsqueda
  const handleTipoBusquedaChange = (event, newTipo) => {
    if (newTipo !== null) {
      setTipoBusqueda(newTipo);
      // Limpiar campos al cambiar el tipo de búsqueda
      setFormBusqueda({ placa: "", vin: "" });
    }
  };

  // Cambiar placa o VIN
  const handleChangeBusqueda = (e) => {
    const { name, value } = e.target;
    if (name === "vin") {
      // Permitir solo números y limitar a 4 dígitos
      const numericValue = value.replace(/\D/g, "").slice(0, 4);
      setFormBusqueda((prev) => ({ ...prev, [name]: numericValue }));
    } else {
      setFormBusqueda((prev) => ({ ...prev, [name]: value.toUpperCase() }));
    }
  };

  // Manejar cambio en checklist de salida
  const handleChecklistChange = (item) => (e) => {
    setFormSalida({ ...formSalida, [item]: e.target.checked });
  };

  // Manejar cambio en checklist de retorno
  const handleChecklistRetornoChange = (item) => (e) => {
    setFormRetorno({ ...formRetorno, [item]: e.target.checked });
  };

  // Verificar si el checklist de salida está completo
  const isChecklistCompleto = () => {
    return (
      formSalida.llanta &&
      formSalida.jack &&
      formSalida.llave &&
      formSalida.triangulos &&
      formSalida.extintor
    );
  };

  // Verificar si el checklist de retorno está completo
  const isChecklistRetornoCompleto = () => {
    return (
      formRetorno.llanta &&
      formRetorno.jack &&
      formRetorno.llave &&
      formRetorno.triangulos &&
      formRetorno.extintor
    );
  };

  // Obtener elementos faltantes del checklist de salida
  const getElementosFaltantes = () => {
    const faltantes = [];
    if (!formSalida.llanta) faltantes.push("Llanta de repuesto");
    if (!formSalida.jack) faltantes.push("Gato (Jack)");
    if (!formSalida.llave) faltantes.push("Llave de ruedas");
    if (!formSalida.triangulos) faltantes.push("Triángulos de seguridad");
    if (!formSalida.extintor) faltantes.push("Extintor");
    return faltantes;
  };

  // Obtener elementos faltantes del checklist de retorno
  const getElementosFaltantesRetorno = () => {
    const faltantes = [];
    if (!formRetorno.llanta) faltantes.push("Llanta de repuesto");
    if (!formRetorno.jack) faltantes.push("Gato (Jack)");
    if (!formRetorno.llave) faltantes.push("Llave de ruedas");
    if (!formRetorno.triangulos) faltantes.push("Triángulos de seguridad");
    if (!formRetorno.extintor) faltantes.push("Extintor");
    return faltantes;
  };

  // Función para obtener nombre del conductor
  const getNombreConductor = (conductorId) => {
    const conductor = conductores.find((c) => c.id === conductorId);
    return conductor
      ? `${conductor.nombre} ${conductor.apellido}`
      : `Conductor ID: ${conductorId}`;
  };

  // Función para obtener información completa del conductor
  const getConductorInfo = (conductorId) => {
    return conductores.find((c) => c.id === conductorId);
  };

  // Función para obtener las rutas
  const getNombreRuta = (rutaId) => {
    const ruta = rutas.find((c) => c.id === rutaId);
    return ruta ? `${ruta.nombre} - ${ruta.direccion}` : `Ruta ID: ${rutaId}`;
  };

  // Buscar vehículo + pase por placa o VIN
  const handleBuscarVehiculo = async () => {
    try {
      if (tipoBusqueda === "placa" && !formBusqueda.placa.trim()) {
        showSnackbar("Ingrese una placa válida", "error");
        return;
      }

      if (tipoBusqueda === "vin" && !formBusqueda.vin.trim()) {
        showSnackbar("Ingrese un VIN válido", "error");
        return;
      }

      // Validar que el VIN tenga exactamente 4 dígitos
      if (tipoBusqueda === "vin" && !/^\d{4}$/.test(formBusqueda.vin.trim())) {
        showSnackbar("El VIN debe contener exactamente 4 dígitos", "error");
        return;
      }

      setLoading(true);

      let url = "";
      if (tipoBusqueda === "placa") {
        url = `https://bei360.injupemp.gob.hn:8444/api/vehiculos/${formBusqueda.placa.trim()}/con-pase/`;
      } else {
        url = `https://bei360.injupemp.gob.hn:8444/api/vehiculos/vin/${formBusqueda.vin.trim()}/con-pase/`;
      }

      const response = await fetch(url);
      if (!response.ok) throw new Error("Vehículo o pase no encontrado");

      const data = await response.json();

      // Si la búsqueda por VIN devuelve un array, tomar el primer elemento
      const resultado = Array.isArray(data) ? data[0] : data;

      if (!resultado) throw new Error("Vehículo o pase no encontrado");

      setVehiculoInfo(resultado.vehiculo);
      setPaseInfo(resultado.pase);

      // Si el pase está en ruta, cargar registro de salida existente
      if (resultado.pase && resultado.pase.estado === "en_ruta") {
        await cargarRegistroSalida(resultado.pase.id);
      }

      // Si el pase está finalizado, cargar histórico
      if (resultado.pase && resultado.pase.estado === "completado") {
        await cargarHistorico(resultado.pase.id);
      }
    } catch (err) {
      showSnackbar(err.message, "error");
      setVehiculoInfo(null);
      setPaseInfo(null);
      setHistoricoRegistros([]);
      setRegistroSalidaExistente(null);
    } finally {
      setLoading(false);
    }
  };

  // Cargar registro de salida existente
  const cargarRegistroSalida = async (paseId) => {
    try {
      const response = await fetch(
        `https://bei360.injupemp.gob.hn:8444/api/registro-inicio/checklist-salida/${paseId}/`
      );

      if (response.ok) {
        const data = await response.json();
        setRegistroSalidaExistente(data);
        // Pre-cargar el kilometraje inicial en el formulario de salida para referencia
        setFormSalida((prev) => ({
          ...prev,
          kilometraje_inicio: data.kilometraje_inicio.toString(),
        }));
      } else if (response.status === 404) {
        setRegistroSalidaExistente(null);
      }
    } catch (err) {
      console.error("Error al cargar registro de salida:", err);
      setRegistroSalidaExistente(null);
    }
  };

  // Cargar histórico de registros
  const cargarHistorico = async (paseId) => {
    try {
      const response = await fetch(
        `https://bei360.injupemp.gob.hn:8444/api/registros-pase/${paseId}/`
      );
      if (response.ok) {
        const data = await response.json();
        setHistoricoRegistros(data);
      }
    } catch (err) {
      console.error("Error al cargar histórico:", err);
    }
  };

  // Comparación de checklists
  const renderComparacionChecklists = () => {
    if (!registroSalidaExistente) return null;

    const items = [
      { key: "llanta", label: "Llanta" },
      { key: "jack", label: "Gato" },
      { key: "llave", label: "Llave" },
      { key: "triangulos", label: "Triángulos" },
      { key: "extintor", label: "Extintor" },
    ];

    return (
      <Paper sx={{ p: 2, mb: 3, bgcolor: "#f0f8ff" }}>
        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: "bold" }}>
          Comparación: Checklist de Salida vs Retorno
        </Typography>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(5, 1fr)",
            gap: 1,
          }}
        >
          {items.map((item) => {
            const salida = registroSalidaExistente[item.key];
            const retorno = formRetorno[item.key];
            const cambio = salida !== retorno;

            return (
              <Box
                key={item.key}
                sx={{
                  textAlign: "center",
                  p: 1,
                  borderRadius: 1,
                  bgcolor: cambio ? "#fff3e0" : "#f5f5f5",
                  border: `1px solid ${cambio ? "#ffa726" : "#e0e0e0"}`,
                }}
              >
                <Typography variant="caption" display="block" fontWeight="bold">
                  {item.label}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    gap: 1,
                    mt: 0.5,
                  }}
                >
                  <Box sx={{ textAlign: "center" }}>
                    <Typography
                      variant="caption"
                      display="block"
                      color="text.secondary"
                    >
                      Salida
                    </Typography>
                    <Typography
                      variant="caption"
                      color={salida ? "success.main" : "error.main"}
                      fontWeight="bold"
                    >
                      {salida ? "✓" : "✗"}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: "center" }}>
                    <Typography
                      variant="caption"
                      display="block"
                      color="text.secondary"
                    >
                      Retorno
                    </Typography>
                    <Typography
                      variant="caption"
                      color={retorno ? "success.main" : "error.main"}
                      fontWeight="bold"
                    >
                      {retorno ? "✓" : "✗"}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>

        <Typography
          variant="caption"
          sx={{ mt: 1, display: "block", fontStyle: "italic" }}
        >
          Fecha de salida:{" "}
          {new Date(registroSalidaExistente.hora_salida).toLocaleString()}
        </Typography>
      </Paper>
    );
  };

  // Actualizar estado del conductor
  const actualizarEstadoConductor = async (conductorId, estado) => {
    try {
      const response = await fetch(
        `https://bei360.injupemp.gob.hn:8444/api/conductores/${conductorId}/`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ estado }),
        }
      );

      if (!response.ok) {
        console.error("Error al actualizar estado del conductor");
      }
    } catch (err) {
      console.error("Error al actualizar conductor:", err);
    }
  };

  // Registrar salida
  const handleRegistrarSalida = async () => {
    try {
      if (
        !paseInfo ||
        !formSalida.kilometraje_inicio ||
        !formSalida.nombre_guardia
      ) {
        showSnackbar("Complete los campos obligatorios", "error");
        return;
      }

      const salidaData = {
        pase: paseInfo.id,
        kilometraje_inicio: parseInt(formSalida.kilometraje_inicio),
        observaciones_salida: formSalida.observaciones_salida,
        hora_salida: formSalida.hora_salida.toISOString(),
        // Campos booleanos del checklist individualmente
        llanta: formSalida.llanta,
        jack: formSalida.jack,
        llave: formSalida.llave,
        triangulos: formSalida.triangulos,
        extintor: formSalida.extintor,
        creado_por: usuarioId,
        nombre_guardia: formSalida.nombre_guardia,
      };

      const response = await fetch(
        "https://bei360.injupemp.gob.hn:8444/api/registro-inicio/",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(salidaData),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const firstErrorKey = Object.keys(errorData)[0];
        const errorMsg = firstErrorKey
          ? errorData[firstErrorKey][0]
          : "El kilometraje ingresado no puede ser menor al kilometraje actual del vehículo";
        throw new Error(errorMsg);
      }

      // Actualizar estado del pase y del conductor a "en_ruta"
      await actualizarEstadoPase(paseInfo.id, "en_ruta");
      await actualizarEstadoConductor(paseInfo.conductor, "en_ruta");

      showSnackbar("Salida registrada exitosamente", "success");
      resetAll();
    } catch (err) {
      showSnackbar(err.message, "error");
    }
  };

  // Registrar retorno
  const handleRegistrarRetorno = async () => {
    try {
      if (
        !paseInfo ||
        !formRetorno.kilometraje_fin ||
        !formRetorno.nombre_guardia
      ) {
        showSnackbar("Complete los campos obligatorios", "error");
        return;
      }

      if (
        parseInt(formRetorno.kilometraje_fin) <=
        parseInt(formSalida.kilometraje_inicio)
      ) {
        showSnackbar("El kilometraje final debe ser mayor al inicial", "error");
        return;
      }

      const retornoData = {
        pase: paseInfo.id,
        kilometraje_fin: parseInt(formRetorno.kilometraje_fin),
        observaciones_retorno: formRetorno.observaciones_retorno,
        hora_retorno: formRetorno.hora_retorno.toISOString(),
        // Campos booleanos del checklist de retorno individualmente
        llanta: formRetorno.llanta,
        jack: formRetorno.jack,
        llave: formRetorno.llave,
        triangulos: formRetorno.triangulos,
        extintor: formRetorno.extintor,
        creado_por: usuarioId,
        nombre_guardia: formRetorno.nombre_guardia,
      };

      const response = await fetch(
        "https://bei360.injupemp.gob.hn:8444/api/registro-fin/",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(retornoData),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        let errorMsg = "Error al registrar retorno";
        if (typeof errorData === "object" && errorData !== null) {
          const firstKey = Object.keys(errorData)[0];
          if (firstKey && Array.isArray(errorData[firstKey])) {
            errorMsg = errorData[firstKey][0];
          } else if (errorData.detail) {
            errorMsg = errorData.detail;
          }
        }
        throw new Error(errorMsg);
      }

      // Actualizar estado del pase a "completado" y del conductor a "Activo"
      await actualizarEstadoPase(paseInfo.id, "completado");
      await actualizarEstadoConductor(paseInfo.conductor, "Activo");

      showSnackbar("Retorno registrada exitosamente", "success");
      resetAll();
    } catch (err) {
      showSnackbar(err.message, "error");
    }
  };

  // Actualizar estado pase
  const actualizarEstadoPase = async (paseId, estado) => {
    await fetch(`https://bei360.injupemp.gob.hn:8444/api/pases/${paseId}/`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ estado }),
    });
  };

  // Resetear todo
  const resetAll = () => {
    setActiveStep(0);
    setVehiculoInfo(null);
    setPaseInfo(null);
    setHistoricoRegistros([]);
    setRegistroSalidaExistente(null);
    setFormBusqueda({ placa: "", vin: "" });
    setTipoBusqueda("placa");
    setFormSalida({
      kilometraje_inicio: "",
      observaciones_salida: "",
      hora_salida: new Date(),
      nombre_guardia: "",
      llanta: false,
      jack: false,
      llave: false,
      triangulos: false,
      extintor: false,
    });
    setFormRetorno({
      kilometraje_fin: "",
      observaciones_retorno: "",
      hora_retorno: new Date(),
      nombre_guardia: "",
      llanta: false,
      jack: false,
      llave: false,
      triangulos: false,
      extintor: false,
    });
  };

  // Notificación
  const showSnackbar = (message, severity) =>
    setSnackbar({ open: true, message, severity });
  const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

  const steps = ["Buscar Vehículo", "Registrar Salida", "Registrar Retorno"];

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
      <Container sx={{ width: 800, mt: 3 }}>
        <Typography
          variant="h4"
          align="center"
          fontWeight="bold"
          sx={{ mb: 4 }}
        >
          Gestión de pases
        </Typography>

        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Paso 0: Búsqueda de vehículo */}
        {activeStep === 0 && (
          <Card sx={{ p: 3 }}>
            {/* Selector de tipo de búsqueda */}
            <Box mb={3}>
              <Typography variant="body2" color="textSecondary" mb={1}>
                Buscar por:
              </Typography>
              <ToggleButtonGroup
                value={tipoBusqueda}
                exclusive
                onChange={handleTipoBusquedaChange}
                fullWidth
              >
                <ToggleButton value="placa">Placa</ToggleButton>
                <ToggleButton value="vin">
                  Últimos 4 dígitos del VIN
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
              {tipoBusqueda === "placa" ? (
                <TextField
                  label="Placa del vehículo"
                  name="placa"
                  value={formBusqueda.placa}
                  onChange={handleChangeBusqueda}
                  fullWidth
                  inputProps={{
                    maxLength: 7,
                    style: { textTransform: "uppercase" },
                  }}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      handleBuscarVehiculo();
                    }
                  }}
                />
              ) : (
                <TextField
                  label="Últimos 4 dígitos del VIN"
                  name="vin"
                  value={formBusqueda.vin}
                  onChange={handleChangeBusqueda}
                  fullWidth
                  inputProps={{ maxLength: 4 }}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      handleBuscarVehiculo();
                    }
                  }}
                />
              )}

              <Button
                variant="contained"
                onClick={handleBuscarVehiculo}
                disabled={loading}
                sx={{ minWidth: 120 }}
              >
                {loading ? <CircularProgress size={24} /> : "Buscar"}
              </Button>
            </Box>

            {vehiculoInfo && paseInfo && (
              <Paper sx={{ p: 2, bgcolor: "#f5f5f5" }}>
                <Typography variant="subtitle2">Vehículo:</Typography>
                <Typography>Placa: {vehiculoInfo.placa}</Typography>
                <Typography>VIN: {vehiculoInfo.vin}</Typography>
                <Typography>Marca: {vehiculoInfo.marca}</Typography>
                <Typography>Modelo: {vehiculoInfo.modelo}</Typography>
                <Typography>Kilometraje: {vehiculoInfo.kilometraje}</Typography>
                <Typography>
                  Estado:
                  <Chip
                    label={vehiculoInfo.estado}
                    color={
                      vehiculoInfo.estado === "Activo" ? "success" : "primary"
                    }
                    sx={{ ml: 1 }}
                  />
                </Typography>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2">Pase asociado:</Typography>
                <Typography>
                  Pase #{paseInfo.id} - {paseInfo.motivo}
                </Typography>

                {/* Información del conductor con estado */}
                {(() => {
                  const conductorInfo = getConductorInfo(paseInfo.conductor);
                  return (
                    <Typography>
                      Conductor: {getNombreConductor(paseInfo.conductor)}
                      {conductorInfo && (
                        <Chip
                          label={conductorInfo.estado}
                          color={
                            conductorInfo.estado === "Activo"
                              ? "success"
                              : conductorInfo.estado === "en_ruta"
                              ? "primary"
                              : "default"
                          }
                          sx={{ ml: 1 }}
                          size="small"
                        />
                      )}
                    </Typography>
                  );
                })()}

                <Typography>Ruta: {getNombreRuta(paseInfo.ruta)}</Typography>
                <Typography>
                  Estado del pase:
                  <Chip
                    label={paseInfo.estado}
                    color={
                      paseInfo.estado === "abierto"
                        ? "warning"
                        : paseInfo.estado === "en_ruta"
                        ? "primary"
                        : "success"
                    }
                    sx={{ ml: 1 }}
                  />
                </Typography>

                {registroSalidaExistente && (
                  <>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle2">
                      Información de salida:
                    </Typography>
                    <Typography>
                      Kilometraje inicial:{" "}
                      {registroSalidaExistente.kilometraje_inicio} km
                    </Typography>
                    <Typography>
                      Hora de salida:{" "}
                      {new Date(
                        registroSalidaExistente.hora_salida
                      ).toLocaleString()}
                    </Typography>
                    <Typography>
                      Guardia: {registroSalidaExistente.nombre_guardia}
                    </Typography>
                  </>
                )}
              </Paper>
            )}

            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
              {paseInfo && paseInfo.estado === "abierto" && (
                <Button variant="contained" onClick={() => setActiveStep(1)}>
                  Registrar Salida
                </Button>
              )}
              {paseInfo && paseInfo.estado === "en_ruta" && (
                <Button variant="contained" onClick={() => setActiveStep(2)}>
                  Registrar Retorno
                </Button>
              )}
              {paseInfo && paseInfo.estado === "completado" && (
                <Button variant="outlined" onClick={resetAll}>
                  Buscar otro vehículo
                </Button>
              )}
            </Box>
          </Card>
        )}

        {/* Paso 1: Registrar salida (solo para pases abiertos) */}
        {activeStep === 1 && paseInfo && paseInfo.estado === "abierto" && (
          <Card sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 3 }} gutterBottom>
              Registrar Salida
            </Typography>

            <TextField
              label="Kilometraje Inicial"
              type="number"
              value={formSalida.kilometraje_inicio}
              onChange={(e) =>
                setFormSalida({
                  ...formSalida,
                  kilometraje_inicio: e.target.value,
                })
              }
              fullWidth
              sx={{ mb: 2 }}
              required
            />

            <TimePicker
              label="Hora de Salida"
              value={formSalida.hora_salida}
              onChange={(date) =>
                setFormSalida({ ...formSalida, hora_salida: date })
              }
              renderInput={(params) => <TextField {...params} fullWidth />}
              sx={{ mb: 2 }}
            />

            {/* Checklist de seguridad */}
            <Paper sx={{ p: 3, mb: 4 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  cursor: "pointer",
                }}
                onClick={() => setChecklistExpanded(!checklistExpanded)}
              >
                <Typography variant="h6">
                  Checklist de Seguridad - Marque los elementos presentes
                </Typography>
                {checklistExpanded ? <ExpandLess /> : <ExpandMore />}
              </Box>

              <Collapse in={checklistExpanded}>
                <FormGroup sx={{ mt: 4 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formSalida.llanta}
                        onChange={handleChecklistChange("llanta")}
                      />
                    }
                    label="Llanta de repuesto"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formSalida.jack}
                        onChange={handleChecklistChange("jack")}
                      />
                    }
                    label="Gato (Jack)"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formSalida.llave}
                        onChange={handleChecklistChange("llave")}
                      />
                    }
                    label="Llave de ruedas"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formSalida.triangulos}
                        onChange={handleChecklistChange("triangulos")}
                      />
                    }
                    label="Triángulos de seguridad"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formSalida.extintor}
                        onChange={handleChecklistChange("extintor")}
                      />
                    }
                    label="Extintor"
                  />
                </FormGroup>

                {!isChecklistCompleto() && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    Checklist incompleto. Los elementos no marcados se
                    registrarán como faltantes.
                  </Alert>
                )}
              </Collapse>
            </Paper>

            <TextField
              label="Observaciones"
              multiline
              rows={3}
              value={formSalida.observaciones_salida}
              onChange={(e) =>
                setFormSalida({
                  ...formSalida,
                  observaciones_salida: e.target.value,
                })
              }
              fullWidth
              sx={{ mb: 2 }}
              placeholder="Agregue observaciones adicionales aquí..."
            />
            <TextField
              label="Ingrese su nombre"
              value={formSalida.nombre_guardia}
              onChange={(e) =>
                setFormSalida({ ...formSalida, nombre_guardia: e.target.value })
              }
              fullWidth
              sx={{ mb: 2 }}
              required
            />

            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Button variant="outlined" onClick={() => setActiveStep(0)}>
                Volver
              </Button>
              <Button
                variant="contained"
                startIcon={<DirectionsCar />}
                onClick={() =>
                  setConfirmDialog({
                    open: true,
                    title: "Confirmar Salida",
                    message: !isChecklistCompleto()
                      ? `Checklist incompleto. ¿Desea continuar con el registro? Elementos faltantes: ${getElementosFaltantes().join(
                          ", "
                        )}`
                      : "Checklist completo. ¿Registrar salida del vehículo?",
                    onConfirm: handleRegistrarSalida,
                  })
                }
                disabled={
                  !formSalida.kilometraje_inicio || !formSalida.nombre_guardia
                }
              >
                Registrar Salida
              </Button>
            </Box>
          </Card>
        )}

        {/* Paso 2: Registrar retorno (solo para pases en ruta) */}
        {activeStep === 2 && paseInfo && paseInfo.estado === "en_ruta" && (
          <Card sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 3 }} gutterBottom>
              Registrar Retorno
            </Typography>

            {registroSalidaExistente && (
              <Paper sx={{ p: 2, mb: 2, bgcolor: "#e8f5e9" }}>
                <Typography variant="subtitle2" fontWeight="bold">
                  Información de salida registrada:
                </Typography>
                <Typography>
                  Kilometraje inicial:{" "}
                  {registroSalidaExistente.kilometraje_inicio} km
                </Typography>
                <Typography>
                  Hora de salida:{" "}
                  {new Date(
                    registroSalidaExistente.hora_salida
                  ).toLocaleString()}
                </Typography>
                <Typography>
                  Guardia de salida: {registroSalidaExistente.nombre_guardia}
                </Typography>
              </Paper>
            )}

            {renderComparacionChecklists()}

            <TextField
              label="Kilometraje Final"
              type="number"
              value={formRetorno.kilometraje_fin}
              onChange={(e) =>
                setFormRetorno({
                  ...formRetorno,
                  kilometraje_fin: e.target.value,
                })
              }
              fullWidth
              sx={{ mb: 2 }}
              required
              inputProps={{
                min: registroSalidaExistente
                  ? parseInt(registroSalidaExistente.kilometraje_inicio) + 1
                  : 0,
              }}
            />

            <TimePicker
              label="Hora de Retorno"
              value={formRetorno.hora_retorno}
              onChange={(date) =>
                setFormRetorno({ ...formRetorno, hora_retorno: date })
              }
              slotProps={{
                textField: { fullWidth: true },
              }}
              sx={{ mb: 2 }}
            />

            {/* Checklist de seguridad de retorno */}
            <Paper sx={{ p: 3, mb: 4 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  cursor: "pointer",
                }}
                onClick={() =>
                  setChecklistRetornoExpanded(!checklistRetornoExpanded)
                }
              >
                <Typography variant="h6">
                  Checklist de Seguridad al Retorno - Marque los elementos
                  presentes
                </Typography>
                {checklistRetornoExpanded ? <ExpandLess /> : <ExpandMore />}
              </Box>

              <Collapse in={checklistRetornoExpanded}>
                <FormGroup sx={{ mt: 4 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formRetorno.llanta}
                        onChange={handleChecklistRetornoChange("llanta")}
                      />
                    }
                    label="Llanta de repuesto"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formRetorno.jack}
                        onChange={handleChecklistRetornoChange("jack")}
                      />
                    }
                    label="Gato (Jack)"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formRetorno.llave}
                        onChange={handleChecklistRetornoChange("llave")}
                      />
                    }
                    label="Llave de ruedas"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formRetorno.triangulos}
                        onChange={handleChecklistRetornoChange("triangulos")}
                      />
                    }
                    label="Triángulos de seguridad"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formRetorno.extintor}
                        onChange={handleChecklistRetornoChange("extintor")}
                      />
                    }
                    label="Extintor"
                  />
                </FormGroup>

                {!isChecklistRetornoCompleto() && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    Checklist incompleto. Los elementos no marcados se
                    registrarán como faltantes.
                  </Alert>
                )}
              </Collapse>
            </Paper>

            <TextField
              label="Observaciones"
              multiline
              rows={3}
              value={formRetorno.observaciones_retorno}
              onChange={(e) =>
                setFormRetorno({
                  ...formRetorno,
                  observaciones_retorno: e.target.value,
                })
              }
              fullWidth
              sx={{ mb: 2 }}
              placeholder="Describa cualquier observación sobre el estado del vehículo al retorno..."
            />
            <TextField
              label="Ingrese su nombre"
              value={formRetorno.nombre_guardia}
              onChange={(e) =>
                setFormRetorno({
                  ...formRetorno,
                  nombre_guardia: e.target.value,
                })
              }
              fullWidth
              sx={{ mb: 2 }}
              required
            />
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Button variant="outlined" onClick={() => setActiveStep(0)}>
                Volver
              </Button>
              <Button
                variant="contained"
                startIcon={<CheckCircle />}
                onClick={() =>
                  setConfirmDialog({
                    open: true,
                    title: "Confirmar Retorno",
                    message: !isChecklistRetornoCompleto()
                      ? `Checklist incompleto. ¿Desea continuar con el registro? Elementos faltantes: ${getElementosFaltantesRetorno().join(
                          ", "
                        )}`
                      : "Checklist completo. ¿Registrar retorno del vehículo?",
                    onConfirm: handleRegistrarRetorno,
                  })
                }
                disabled={
                  !formRetorno.kilometraje_fin || !formRetorno.nombre_guardia
                }
              >
                Registrar Retorno
              </Button>
            </Box>
          </Card>
        )}

        {/* Diálogo de confirmación */}
        <Dialog
          open={confirmDialog.open}
          onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
        >
          <DialogTitle>{confirmDialog.title}</DialogTitle>
          <DialogContent>
            <Typography>{confirmDialog.message}</Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() =>
                setConfirmDialog({ ...confirmDialog, open: false })
              }
            >
              Cancelar
            </Button>
            <Button
              onClick={() => {
                confirmDialog.onConfirm();
                setConfirmDialog({ ...confirmDialog, open: false });
              }}
              variant="contained"
            >
              Confirmar
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={5000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert severity={snackbar.severity} onClose={handleCloseSnackbar}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </LocalizationProvider>
  );
};

export default GestionPases;
